import { useState, useRef, useCallback } from "react";

const TURNSTILE_SITE_KEY = "0x4AAAAAABCZRvADQrVNADOF";

const useTurnstile = (showSnackbar, themeMode) => {
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileScriptLoaded = useRef(false);
  const turnstileWidgetId = useRef(null); // To store the widget ID for reset

  const handleTurnstileVerify = useCallback((token) => {
    console.log(
      "Turnstile onVerify:",
      token ? "Valid token received" : "No token"
    );
    setTurnstileToken(token);
  }, []);

  const renderTurnstileWidget = useCallback(() => {
    // Skip rendering if token already exists or widget is already rendered
    if (turnstileToken || turnstileWidgetId.current) {
      console.log("Turnstile token or widget already exists, skipping render");
      return;
    }

    if (!window.turnstile) {
      console.error("Turnstile not available");
      return;
    }

    const container = document.getElementById("turnstile-container");
    if (!container) {
      console.error("Turnstile container not found");
      return;
    }

    // Clear any existing widgets
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    console.log(
      "Rendering Turnstile widget with site key:",
      TURNSTILE_SITE_KEY
    );

    try {
      turnstileWidgetId.current = window.turnstile.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: handleTurnstileVerify,
        "expired-callback": () => {
          console.log("Turnstile token expired");
          setTurnstileToken(null);
          turnstileWidgetId.current = null; // Clear widget ID on expiration
        },
        "error-callback": (error) => {
          console.error("Turnstile error:", error);
          showSnackbar(
            "Security verification error. Please try again.",
            "error"
          );
          setTurnstileToken(null);
          turnstileWidgetId.current = null;
        },
        theme: themeMode,
      });
    } catch (error) {
      console.error("Error rendering Turnstile widget:", error);
      showSnackbar(
        "Failed to initialize security verification. Please refresh the page.",
        "error"
      );
      setTurnstileToken(null);
      turnstileWidgetId.current = null;
    }
  }, [handleTurnstileVerify, showSnackbar, turnstileToken, themeMode]);

  const loadTurnstileScript = useCallback(() => {
    if (turnstileScriptLoaded.current) {
      // If script is already loaded, try to render the widget immediately
      setTimeout(() => renderTurnstileWidget(), 50);
      return;
    }

    console.log("Loading Turnstile script manually");
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoaded";
    script.async = true;

    window.onTurnstileLoaded = () => {
      console.log("Turnstile script loaded successfully");
      turnstileScriptLoaded.current = true;
      setTimeout(() => {
        renderTurnstileWidget();
      }, 100);
    };

    script.onerror = (error) => {
      console.error("Error loading Turnstile script:", error);
      showSnackbar(
        "Failed to load security verification. Please refresh the page.",
        "error"
      );
    };

    document.head.appendChild(script);
  }, [renderTurnstileWidget, showSnackbar]);

  const resetTurnstile = useCallback(() => {
    if (window.turnstile && turnstileWidgetId.current) {
      try {
        window.turnstile.reset(turnstileWidgetId.current);
        setTurnstileToken(null);
        turnstileWidgetId.current = null;
      } catch (error) {
        console.error("Error resetting Turnstile:", error);
      }
    }
  }, []);

  return {
    turnstileToken,
    setTurnstileToken,
    loadTurnstileScript,
    resetTurnstile,
    TURNSTILE_SITE_KEY,
  };
};

export default useTurnstile;
